// Copyright 2025, University of Maryland, All Rights Reserved
import { type FC, useCallback, useState } from 'react'
import { FileText, Loader2, Plus, RefreshCw } from 'lucide-react'

import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui'

import type { NodeUserViewProps } from '../../../../../../core/types'
import { useAuth } from '@/subsystems/workspaces'
import { KnowledgeBaseProvider } from '../../../../shared/core/KnowledgeBaseProvider'
import { ViewConfigProvider, useViewConfig } from '../../../../shared/core/ViewConfigContext'
import { REPORT_CONFIG } from '../../../../shared/types'
import { ReportSectionForm } from './ReportSectionForm'
import { ReportSectionList } from './ReportSectionList'
import { useReportSections } from './useReportSections'

interface Props extends NodeUserViewProps {
  wsId?: string
}

/**
 * Report KB User View - Simplified UI for report section management.
 * Uses ViewConfigProvider for configuration, KnowledgeBaseProvider for data.
 */
export const ReportKBUserView: FC<Props> = props => {
  return (
    <ViewConfigProvider config={REPORT_CONFIG}>
      <KnowledgeBaseProvider {...props}>
        <ReportKBScreen workspaceLabel={props.wsId ?? props.workspaceId ?? 'default-workspace'} />
      </KnowledgeBaseProvider>
    </ViewConfigProvider>
  )
}

interface ReportKBScreenProps {
  workspaceLabel: string
}

export function ReportKBScreen({ workspaceLabel }: ReportKBScreenProps): JSX.Element {
  const viewConfig = useViewConfig()
  const { isGlobalAdmin, isWorkspaceAdmin } = useAuth()
  const readOnly = !isGlobalAdmin && !isWorkspaceAdmin
  const [isAdding, setIsAdding] = useState(false)
  const {
    sections,
    editingItem,
    loading,
    saving,
    error,
    addSection,
    submitEdit,
    startEditing,
    cancelEditing,
    deleteSection,
    setError,
    reload,
  } = useReportSections()

  const handleAddSubmit = useCallback(async (data: Parameters<typeof addSection>[0]) => {
    await addSection(data)
    setIsAdding(false)
  }, [addSection])

  const handleStartEditing = useCallback((...args: Parameters<typeof startEditing>) => {
    setIsAdding(false)
    startEditing(...args)
  }, [startEditing])

  return (
    <Card data-testid="report-kb-screen">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              {viewConfig.display.title}
            </CardTitle>
            <CardDescription className="mt-2.5">
              Each section becomes part of your report. The LLM runs each prompt against uploaded documents.
              <span className="text-slate-600"> • {workspaceLabel}</span>
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="text-xs" data-testid="report-sections-count">
              {sections.length} {sections.length === 1 ? 'Section' : 'Sections'}
            </Badge>
            <Button
              aria-label="Refresh"
              variant="ghost"
              size="sm"
              onClick={() => void reload()}
              disabled={loading}
              className="h-8 w-8 p-0"
              data-testid="report-sections-refresh"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-slate-600" />
          </div>
        ) : (
          <>
            {!readOnly && isAdding && (
              <ReportSectionForm
                disabled={loading}
                saving={saving}
                onSubmit={handleAddSubmit}
                onCancel={() => setIsAdding(false)}
                onError={setError}
              />
            )}

            {!readOnly && !isAdding && !editingItem && (
              <Button
                variant="outline"
                onClick={() => setIsAdding(true)}
                disabled={loading || saving}
                data-testid="report-sections-add-trigger"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Section
              </Button>
            )}

            <div className="pt-2" data-testid="report-sections-list-container">
              <h3 className="text-sm font-medium text-slate-600 mb-3">
                Defined Sections
              </h3>
              <ReportSectionList
                items={sections}
                disabled={loading || saving}
                readOnly={readOnly}
                editingItem={editingItem}
                saving={saving}
                onEdit={handleStartEditing}
                onDelete={deleteSection}
                onSubmitEdit={submitEdit}
                onCancelEdit={cancelEditing}
                onError={setError}
              />
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}
